import type { DateTime } from 'luxon';
import Alert from '../../../../components/alert/Alert';
import classnames from 'classnames';
import { useQuery } from 'react-query';
import { QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import { renderMandrillTemplate } from '../../../../helper/handlebars';
import { useI18n } from '@whizz/react-i18n';

const ORG_TEMPLATE = 'organization-weekly-leaders-en-gb';

export default function OrganizationEmailPreview({ previewData }: Props) {
  const { translate: t } = useI18n();

  const getMailchimpRenderedTemplateQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_MAILCHIMP_RENDERED_TEMPLATE, { template: ORG_TEMPLATE } ],
    queryFn: () => repository.AdminRepository.getMailchimpRenderedTemplate(ORG_TEMPLATE, { locale: 'en-gb' }),
  });

  const getPreviewDynamicDataQuery = useQuery({
    queryKey: [
      QUERIES_IDS.ADMIN.GET_ORGANISATION_WEEKLY_LEADERS_EMAIL_PREVIEW_DYNAMIC_DATA,
      {
        organization: previewData.organization?.id,
        startDate: previewData.startDate,
        endDate: previewData.endDate,
      },
    ],
    queryFn: () => {
      if (!previewData.organization) {
        throw new Error('Organization is null');
      }
      return repository.AdminRepository.getOrganizationWeeklyLeadersEmailPreviewDynamicData(
        previewData.organization.id,
        previewData.startDate!,
        previewData.endDate!,
      );
    },
    enabled: previewData.organization !== null && previewData.startDate !== null && previewData.endDate !== null,
  });

  return (
    <div className={'flex flex-col gap-5'}>
      <h2 className={'text-xl font-bold'}>
        {t('pages.ADMIN.PREVIEW.TITLE')}
      </h2>

      {!previewData.isEnabled && (
        <Alert show type={'warning'}>
          {t('pages.ADMIN.PREVIEW.DISABLED')}
        </Alert>
      )}

      <div className={classnames({ 'opacity-50': !previewData.isEnabled })}>
        {getMailchimpRenderedTemplateQuery.isSuccess && (
          <div
            dangerouslySetInnerHTML={{
              __html: renderMandrillTemplate(
                getMailchimpRenderedTemplateQuery.data.data,
                {
                  organization: {
                    name: previewData.organization?.name,
                  },
                  startDate: {
                    weekday: previewData.startDate?.setLocale('en-gb').toFormat('cccc'),
                    dayMonth: previewData.startDate?.setLocale('en-gb').toFormat('dd MMMM'),
                  },
                  endDate: {
                    weekday: previewData.endDate?.setLocale('en-gb').toFormat('cccc'),
                    dayMonth: previewData.endDate?.setLocale('en-gb').toFormat('dd MMMM'),
                  },
                  contacts: previewData.contacts.join(', '),
                  standardGreeting: previewData.standardGreeting,
                  message: previewData.message?.split('\n').join('<br />'),
                  reminderMessage: previewData.reminderMessage?.split('\n').join('<br />'),
                  metricsComponents: previewData.metricsComponents.map((metricsComponent) => ({
                    type: metricsComponent,
                    value: (getPreviewDynamicDataQuery.data?.data.stats.metricsComponents as Record<string, { value: number; previousValue: number; total?: number }>)?.[metricsComponent]?.value ?? 0,
                    ...(metricsComponent === 'hall_of_fame' ? { total: (getPreviewDynamicDataQuery.data?.data.stats.metricsComponents as Record<string, { value: number; previousValue: number; total?: number }>)?.[metricsComponent]?.total } : {}),
                    previousValue: (getPreviewDynamicDataQuery.data?.data.stats.metricsComponents as Record<string, { value: number; previousValue: number; total?: number }>)?.[metricsComponent]?.previousValue ?? 0,
                  })),
                  schools: getPreviewDynamicDataQuery.data?.data.stats.schools ?? [],
                },
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}

interface Props {
  previewData: PreviewData;
}

export interface PreviewData {
  isEnabled: boolean;
  organization: { id: number; name: string } | null;
  startDate: DateTime | null;
  endDate: DateTime | null;
  contacts: string[];
  standardGreeting: boolean;
  message: string | null;
  reminderMessage: string | null;
  metricsComponents: ('usage' | 'progressions' | 'hall_of_fame')[];
}
